import { Injectable } from '@angular/core';
import { FormContext, Is, Trigger } from '@wdx/shared/utils';
import { DateTime } from 'luxon';
import * as R from 'ramda';

@Injectable({
    providedIn: 'root',
})
export class FormTriggersService {
    is(is: Is, triggerFieldValue: any): boolean {
        return !!(
            (Object.hasOwnProperty.call(is, 'equalTo') &&
                !Array.isArray(triggerFieldValue) &&
                triggerFieldValue === is.equalTo) ||
            (Object.hasOwnProperty.call(is, 'equalTo') &&
                Array.isArray(triggerFieldValue) &&
                triggerFieldValue.includes(is.equalTo)) ||
            (is.anyOf &&
                !Array.isArray(triggerFieldValue) &&
                is.anyOf.includes(triggerFieldValue)) ||
            (is.anyOf &&
                Array.isArray(triggerFieldValue) &&
                is.anyOf.some((r) => triggerFieldValue.includes(r))) ||
            (is.future &&
                !triggerFieldValue?.start &&
                DateTime.fromJSDate(new Date(triggerFieldValue))
                    .diffNow('milliseconds')
                    .as('milliseconds') > 0) ||
            (is.future &&
                triggerFieldValue?.start &&
                DateTime.fromJSDate(new Date(triggerFieldValue?.start))
                    .diffNow('milliseconds')
                    .as('milliseconds') > 0) ||
            (is.past &&
                !triggerFieldValue?.start &&
                DateTime.fromJSDate(new Date(triggerFieldValue))
                    .diffNow('milliseconds')
                    .as('milliseconds') < 0) ||
            (is.past &&
                triggerFieldValue?.start &&
                DateTime.fromJSDate(new Date(triggerFieldValue?.start))
                    .diffNow('milliseconds')
                    .as('milliseconds') < 0) ||
            (is.lesserThan && triggerFieldValue < is.lesserThan) ||
            (is.greaterThan && triggerFieldValue > is.greaterThan) ||
            (is.empty &&
                (R.isEmpty(triggerFieldValue) || R.isNil(triggerFieldValue))) ||
            (is.notEmpty &&
                R.isNotEmpty(triggerFieldValue) &&
                R.isNotNil(triggerFieldValue)) ||
            null
        );
    }

    useContext(trigger: Trigger) {
        return trigger.field?.startsWith('$');
    }

    /**
     * Looks up a value from context object using dot notation from a trigger field value
     *
     * Supports objects and array notation
     *
     * { party: { title: 'Party Title', roles: [ {type: 'Role A'}, {type: 'Role B'}] } ] } }
     *
     * $party.title = 'Party Title'
     *
     * $party.roles[].type = ['Role A', 'Role B']
     */

    getContextTriggerValue(trigger: Trigger, context: FormContext) {
        return this.getContextValue(
            trigger.field?.substring(1) as string,
            context,
        );
    }

    public findTriggersThatWatchField(
        triggers: Trigger[] | null | undefined,
        fieldName: string,
    ): Trigger[] {
        if (!triggers?.length || !fieldName) {
            return [];
        }

        return triggers?.filter((trigger) => trigger.field === fieldName);
    }

    public findTriggersThatUpdateField(
        triggers: Trigger[] | null | undefined,
        fieldName: string,
        startsWith = false,
    ): Trigger[] {
        if (!triggers?.length || !fieldName) {
            return [];
        }

        return triggers?.filter((trigger) =>
            trigger.is?.some(
                (is) =>
                    is?.then?.updateFields?.some(({ name }) =>
                        startsWith
                            ? name?.startsWith(`${fieldName}.`)
                            : name === fieldName,
                    ) ||
                    is?.else?.updateFields?.some(({ name }) =>
                        startsWith
                            ? name?.startsWith(`${fieldName}.`)
                            : name === fieldName,
                    ),
            ),
        );
    }

    private getContextValue(field: string, context: FormContext) {
        const parts = field?.split('.');
        parts?.forEach((fieldPart, i) => {
            const isArrayPart = fieldPart.endsWith('[]');
            if (isArrayPart) {
                const arrayKey = fieldPart.replace('[]', '');
                // eslint-disable-next-line no-prototype-builtins
                if (context.hasOwnProperty(arrayKey)) {
                    context = (context as any)[arrayKey].map(
                        (arrayField: FormContext) => {
                            return this.getContextValue(
                                parts.slice(i + 1).join('.'),
                                arrayField,
                            );
                        },
                    );
                }
            } else {
                // eslint-disable-next-line no-prototype-builtins
                if (context?.hasOwnProperty(fieldPart)) {
                    context = (context as any)[fieldPart];
                }
            }
        });
        return context;
    }
}
